import { deleteBlockedError } from './deleteBlocked'
import { http } from '@/api/http'
import { toListParams, type ListQuery } from '@/api/query'
import type {
  ContentLocale,
  ListResponse,
  ProcurementCustomsAccount,
  ProcurementCustomsDeclaration,
  ProcurementCustomsDeclarationHistory,
  ProcurementCustomsDeleteCheck,
  ProcurementCustomsEligibleOption,
  ProcurementCustomsFilterOptions,
  ProcurementCustomsTurnover,
} from '@/types/api'
import type { RequestOptions } from '../crud'
import { reportLanguage } from './reports.service'

export type ProcurementCustomsAttachmentInput = { url: string; name?: string }

export type ProcurementCustomsMutableInput = {
  country_id: string
  customs_location_id: string
  currency_id: string
  amount: string
  declaration_number?: string
  declaration_at?: string
  border_crossed_at?: string
  note?: string
  attachments: ProcurementCustomsAttachmentInput[]
}

export type CreateProcurementCustomsInput = ProcurementCustomsMutableInput & {
  idempotency_key: string
  batch_id: string
  transport_unit_id: string
}

export type ReplaceProcurementCustomsInput = ProcurementCustomsMutableInput & { version: number }
export type PatchProcurementCustomsInput = Partial<ProcurementCustomsMutableInput> & { version: number }
/** The modal's «Сохранить» / «Оплатить»: omitted fields keep their value; cleared («Оплатить») posts the customs payment. */
export type SaveProcurementCustomsInput = {
  version: number
  amount?: string
  customs_account_id?: string
  declaration_at?: string
  attachments?: ProcurementCustomsAttachmentInput[]
  cleared: boolean
}

const base = '/procurement/customs-declarations'

export const procurementCustomsService = {
  path: 'procurement/customs-declarations',
  async list(query?: ListQuery, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementCustomsDeclaration>>(base, {
      params: toListParams(query),
      ...options,
    })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async get(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementCustomsDeclaration>(`${base}/${encodeURIComponent(id)}`, options)
    return data
  },
  async create(body: CreateProcurementCustomsInput) {
    const { idempotency_key, ...command } = body
    const { data } = await http.post<ProcurementCustomsDeclaration>(base, command, {
      headers: { 'Idempotency-Key': idempotency_key },
    })
    return data
  },
  async replace(id: string, body: ReplaceProcurementCustomsInput) {
    const { data } = await http.put<ProcurementCustomsDeclaration>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async patch(id: string, body: PatchProcurementCustomsInput) {
    const { data } = await http.patch<ProcurementCustomsDeclaration>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async update(id: string, body: PatchProcurementCustomsInput) {
    return this.patch(id, body)
  },
  async deleteCheck(id: string, options?: RequestOptions) {
    const { data } = await http.get<ProcurementCustomsDeleteCheck>(`${base}/${encodeURIComponent(id)}/delete-check`, options)
    return data
  },
  async remove(id: string) {
    const [current, check] = await Promise.all([this.get(id), this.deleteCheck(id)])
    if (!check.can_delete) {
      throw deleteBlockedError(check.reasons, 412, 'FAILED_PRECONDITION')
    }
    await http.delete(`${base}/${encodeURIComponent(id)}`, { params: { version: current.version } })
  },
  async history(id: string, page = 1, limit = 20, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementCustomsDeclarationHistory>>(
      `${base}/${encodeURIComponent(id)}/history`,
      { params: { page, limit }, ...options },
    )
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  async filterOptions(options?: RequestOptions) {
    const { data } = await http.get<ProcurementCustomsFilterOptions>(`${base}/filter-options`, options)
    return data
  },
  async eligibleOptions(search = '', page = 1, limit = 30, options?: RequestOptions) {
    const { data } = await http.get<ListResponse<ProcurementCustomsEligibleOption>>(`${base}/eligible-options`, {
      params: { search, page, limit },
      ...options,
    })
    return { count: data.count ?? 0, items: data.items ?? [] }
  },
  /** «Удалить оплату»: the payment is reversed in Finance and the declaration is «Не растаможен» again. */
  async deletePayment(id: string, body: { version: number; reason: string }) {
    const { data } = await http.post<ProcurementCustomsDeclaration>(`${base}/${encodeURIComponent(id)}/delete-payment`, body)
    return data
  },
  async save(id: string, body: SaveProcurementCustomsInput) {
    const { data } = await http.post<ProcurementCustomsDeclaration>(`${base}/${encodeURIComponent(id)}/save`, body)
    return data
  },
  /** The active customs accounts («Таможенный счёт») with their balances. */
  async customsAccounts(options?: RequestOptions) {
    const { data } = await http.get<ProcurementCustomsAccount[]>('/procurement/customs-accounts', options)
    return data ?? []
  },
  /** «Таможенные счета»: opening, movements with running balances, closing (from/to are ISO times). */
  async customsTurnover(params: { account_id?: string; from?: string; to?: string }, options?: RequestOptions) {
    const { data } = await http.get<ProcurementCustomsTurnover>('/procurement/customs-accounts/turnover', { params, ...options })
    return data
  },
  /** Names in the file follow the content locale. */
  async export(query: ListQuery, locale: ContentLocale) {
    const { data } = await http.post<Blob>(
      `${base}/export`,
      { search: query.search ?? '', filters: query.filters ?? [], sort: query.sort ?? [], lang: reportLanguage(locale) },
      { responseType: 'blob' },
    )
    return data
  },
}
